import { useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"

import { getIcon } from "utils/icon"

// Components
import { Head } from "components/Head"
import { MotionDiv, showContent } from "components/motions"
import { LanguageButton } from "extensions/iconsiam/components/language/LanguageButton"
import { LuxuryCard } from "extensions/iconsiam/components/domains/luxury/Card"
import { SearchToggleButton } from "components/domains/search"
import { EmptyData } from "components/EmptyData"
import { ServiceIcon } from "components/icons"

import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"

// Swiper
import { Swiper, SwiperSlide } from "swiper/react"
import { EffectCards, Autoplay } from "swiper/modules"
import "swiper/css"
import "swiper/css/effect-cards"
import { getPostDetail } from "utils/feature"
import { useLuxury } from "extensions/iconsiam/hooks/luxury"

const transformLuxury = (data) => {
  const { id, ...rest } = getPostDetail(data)

  return {
    ...rest,
    id,
    locationUrl: `/luxury/${id}`,
  }
}

export const LuxuryKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { hasData, luxurySlides, loop, autoPlay } = useLuxury()
  const { openSearch } = useAppUI()
  const theme = useTheme()
  const { t } = useTranslation()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.luxury) || <ServiceIcon />,
    [theme]
  )
  const styleOverrides = theme.components[`LuxuryPage`]?.styleOverrides || {}
  const {
    root,
    emptyDataWrapper,
    navigationWrapper,
    toolbarContainer,
    actionButton,
    actionContainer,
    searchBarContainer,
    swiperWrapper,
  } = styleOverrides

  const direction = theme.direction

  return (
    <>
      <Head title={t("Luxury")} />
      <Layout>
        <Box sx={root}>
          <MotionDiv animate="animate" initial="initial" variants={showContent}>
            {hasData ? (
              <>
                <Box sx={swiperWrapper}>
                  <Swiper
                    key={`luxury-swiper-${direction}`}
                    effect={"cards"}
                    grabCursor={true}
                    modules={[EffectCards, Autoplay]}
                    autoplay={autoPlay}
                    className="LuxuryPage-swiper"
                    loop={loop}
                    speed={750}
                    dir={direction}
                    cardsEffect={{
                      slideShadows: false,
                      rotate: true,
                      perSlideOffset: 101,
                      perSlideRotate: 4,
                    }}
                    onInit={(swiper) => {
                      const activeSlide = swiper.slides[swiper.activeIndex]
                      if (activeSlide) {
                        activeSlide.style.height = "100%"
                      }
                    }}
                  >
                    {luxurySlides.map((luxury, index) => (
                      <SwiperSlide key={index}>
                        <LuxuryCard data={transformLuxury(luxury)} />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </Box>
              </>
            ) : (
              <>
                <Box sx={emptyDataWrapper}>
                  <EmptyData
                    icon={emptyIcon}
                    primary={t(`No Current Luxury`)}
                    secondary={t("Check Back Soon")}
                  />
                </Box>
              </>
            )}
          </MotionDiv>

          <Box key="app-navigation" sx={navigationWrapper}>
            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageButton sx={actionButton} />
                {additionalMenu}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
